import {
  Alert,
  Autocomplete,
  Button,
  createFilterOptions,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import FormSelectField from "components/FormFields/FormSelect";
import { showToast } from "utils/toast";
import { DEVICE_SUGGESTIONS, TOAST_SEVERITY } from "static/constants";

import SubmitButton from "components/Button/SubmitButton";
import { createDevice, updateDevice } from "app/services/devices/device";
import FormTextField from "components/FormFields/FormTextField";
import { useSelector } from "react-redux";
import { Formik } from "formik";
import { deviceOonfigurationValidationSchema } from "schemas/deviceOonfigurationValidationSchema";
import { broadcastData } from "utils/broadcastChannel";
import { getOptionValue } from "utils/autocomplete";
import { useGetAlertListQuery } from "app/store/devices/deviceApi";

const filter = createFilterOptions();

const DeviceForm = ({ handleClose, selectedDevice, isUpdate }) => {
  const { isLoading, data: alertList } = useGetAlertListQuery(
    {},
    { refetchOnMountOrArgChange: true }
  );
  const initialValues = {
    hf: "false",
    alertText: "",
    chamber: "",
    modelName: "",
    modelDisplayName: "",
    deviceTypeId: "",
    manufacturerId: "",
    deviceStatus: "active",
    modelNameEditable: true,
  };

  const [manufacturers, setManufacturers] = useState([]);
  const [deviceType, setDeviceType] = useState([]);
  const [deviceFormValues, setDeviceFormValues] = useState(initialValues);
  const [isProcessing, setIsProcessing] = useState(false);

  const { manufacturers: manufacturerList } = useSelector(
    (state) => state.manufacturers
  );
  const { deviceTypes } = useSelector((state) => state.deviceTypes);

  useEffect(() => {
    mapManufacturerList();
    mapDeviceTypesList();
  }, []);

  useEffect(() => {
    if (selectedDevice) {
      const {
        hf,
        alert_text,
        chamber,
        deviceTypeId,
        manufacturerId,
        modelName,
        deviceStatus,
        display_name,
        model_name_editable,
      } = selectedDevice;

      const selectedDeviceFormValue = {
        chamber,
        modelName,
        deviceTypeId,
        manufacturerId,
        alertText: alert_text,
        hf: hf ? hf.toString() : "false",
        deviceStatus: deviceStatus ?? "active",
        modelDisplayName: display_name,
        modelNameEditable: model_name_editable,
      };

      setDeviceFormValues(selectedDeviceFormValue);
    }
  }, [selectedDevice]);

  const mapManufacturerList = async () => {
    try {
      const mappedManufacturers = manufacturerList.map((manufacturer) => ({
        value: manufacturer._id,
        label: manufacturer.name,
      }));
      setManufacturers(mappedManufacturers);
    } catch (error) {
      showToast({ message: error.message, type: TOAST_SEVERITY.ERROR });
    }
  };

  const mapDeviceTypesList = async () => {
    try {
      const mappedDeviceTypes = deviceTypes.map((deviceType) => ({
        value: deviceType._id,
        label: deviceType.shortName,
      }));
      setDeviceType(mappedDeviceTypes);
    } catch (error) {
      showToast({ message: error.message, type: TOAST_SEVERITY.ERROR });
    }
  };

  const handleFormSubmit = async (values) => {
    try {
      setIsProcessing(true);

      const hfValue = values.hf === "true";

      const updatedFormValues = {
        ...values,
        hf: hfValue,
        alertText: values.alertText || "",
        display_name: values?.modelDisplayName,
      };

      delete updatedFormValues?.modelDisplayName;

      let response;
      if (isUpdate) {
        const selectedDeviceId = selectedDevice._id;
        response = await updateDevice(selectedDeviceId, updatedFormValues);
      } else {
        response = await createDevice(updatedFormValues);
      }
      if (response.type === "Success") {
        broadcastData(true);
        showToast({ message: response.message });
        handleClose("success");
      }
    } catch (error) {
      showToast({ message: error.message, type: TOAST_SEVERITY.ERROR });
    } finally {
      setIsProcessing(false);
      broadcastData(false);
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={{ ...deviceFormValues }}
      validationSchema={deviceOonfigurationValidationSchema}
      onSubmit={(values) => handleFormSubmit(values)}
    >
      {({
        handleSubmit,
        touched,
        errors,
        values,
        handleChange,
        setFieldValue,
      }) => (
        <>
          <Grid container className="device-section" spacing={1.5}>
            <Grid item xs={12}>
              <h3 className="mt-2">Generate HF Report</h3>
              <FormControl
                error={touched.hf && Boolean(errors.hf)}
                margin="none"
              >
                <RadioGroup
                  row
                  aria-labelledby="demo-row-radio-buttons-group-label"
                  name="row-radio-buttons-group"
                  defaultValue="existing"
                  value={values.hf}
                  onChange={handleChange("hf")}
                >
                  <FormControlLabel
                    value="true"
                    control={<Radio />}
                    label="Yes"
                  />
                  <FormControlLabel
                    value="false"
                    control={<Radio />}
                    label="No"
                  />
                </RadioGroup>
              </FormControl>
              {touched.hf && errors.hf && (
                <FormHelperText error>{errors.hf}</FormHelperText>
              )}
            </Grid>

            <Grid item xs={12}>
              <h3 className="mt-2">Chamber</h3>
              <FormControl
                error={touched.chamber && Boolean(errors.chamber)}
                margin="none"
              >
                <RadioGroup
                  row
                  aria-labelledby="demo-row-radio-buttons-group-label"
                  name="row-radio-buttons-group"
                  defaultValue="existing"
                  value={values.chamber}
                  onChange={handleChange("chamber")}
                >
                  <FormControlLabel value="" control={<Radio />} label="None" />
                  <FormControlLabel
                    value="single"
                    control={<Radio />}
                    label="Single"
                  />
                  <FormControlLabel
                    value="dual"
                    control={<Radio />}
                    label="Dual"
                  />
                  <FormControlLabel
                    value="bi-ventricular"
                    control={<Radio />}
                    label="Bi-ventricular"
                  />
                </RadioGroup>
              </FormControl>
              {touched.chamber && errors.chamber && (
                <FormHelperText error>{errors.chamber}</FormHelperText>
              )}
            </Grid>

            <Grid item xs={12}>
              <FormTextField
                fullWidth
                margin="none"
                value={values.modelName}
                label="Model Name"
                required={true}
                hasError={touched.modelName && Boolean(errors.modelName)}
                inputProps={{ maxLength: 50 }}
                onChange={handleChange("modelName")}
                InputProps={{
                  readOnly: deviceFormValues?.modelNameEditable === false,
                }}
                className="mb-2"
              />
              <Alert severity="warning" className="mt-2">
                Device model name should be in this following format:{" "}
                {DEVICE_SUGGESTIONS.map((device) => (
                  <li key={device}>{device}</li>
                ))}
              </Alert>
            </Grid>

            <Grid item xs={12}>
              <FormTextField
                fullWidth
                value={values.modelDisplayName}
                label="Display Name"
                hasError={
                  touched.modelDisplayName && Boolean(errors.modelDisplayName)
                }
                inputProps={{ maxLength: 50 }}
                onChange={handleChange("modelDisplayName")}
              />
            </Grid>

            <Grid item xs={6}>
              <FormSelectField
                label="Device Type"
                required={true}
                value={values.deviceTypeId}
                options={deviceType}
                hasError={touched.deviceTypeId && Boolean(errors.deviceTypeId)}
                onChange={handleChange("deviceTypeId")}
                margin="none"
              />
            </Grid>
            <Grid item xs={6}>
              <FormSelectField
                label="Manufacturer"
                required={true}
                value={values.manufacturerId}
                options={manufacturers}
                hasError={
                  touched.manufacturerId && Boolean(errors.manufacturerId)
                }
                submitClicked={false}
                onChange={handleChange("manufacturerId")}
                margin="none"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth variant="outlined" margin="none">
                <Autocomplete
                  fullWidth
                  freeSolo
                  id="alertText"
                  name="alertText"
                  value={values?.alertText}
                  filterSelectedOptions
                  limitTags={2}
                  options={
                    alertList?.map((item) => ({
                      label: item,
                      inputValue: item,
                    })) || []
                  }
                  openOnFocus={true}
                  onChange={(_e, newValue) => {
                    setFieldValue("alertText", newValue?.inputValue);
                  }}
                  filterOptions={(options, params) => {
                    const filtered = filter(options, params);
                    if (params.inputValue !== "") {
                      filtered.unshift({
                        inputValue: params.inputValue,
                        label: `+ Add "${params.inputValue}"`,
                      });
                    }

                    return filtered;
                  }}
                  clearOnBlur
                  getOptionLabel={getOptionValue}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Device Alert"
                      variant="outlined"
                      placeholder="Device Alert"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        fullWidth: true,
                        readOnly: isLoading ? true : false,
                      }}
                      inputProps={{
                        ...params.inputProps,
                        maxLength: 100,
                      }}
                    />
                  )}
                />
              </FormControl>
            </Grid>
          </Grid>

          <div
            className="d-flex flex-end"
            style={{ gap: "10px", marginTop: 20 }}
          >
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleClose}
              disabled={isProcessing}
            >
              Cancel
            </Button>

            <SubmitButton
              variant="contained"
              color="secondary"
              handleClick={handleSubmit}
              isLoading={isProcessing}
            />
          </div>
        </>
      )}
    </Formik>
  );
};

export default DeviceForm;
